/**
 * Frontend permissions catalogue.
 *
 * Mirrors the backend ``app.modules.authz.application.permissions``
 * (Python class ``Permissions``) and the catalogue rows seeded by
 * alembic migration ``authz11perm``. Centralises every permission
 * string the portal can reference so that:
 *
 *   1. The admin role editor renders the full catalogue (PERMISSIONS_BY_GROUP)
 *   2. Permission checks at call sites use the same canonical strings
 *      the backend recognises — no copy-paste typos
 *   3. Front-end can preview which permissions a role *effectively*
 *      grants by running the same expansion logic
 *      (``expandPermissionHierarchy``) the backend uses
 *
 * Source of truth — DO NOT diverge from these files without also
 * updating this module:
 *   - ``api/app/modules/authz/application/permissions.py``
 *   - ``api/alembic/versions/authz11_perm_catalog_seed.py``
 *
 * Hierarchy & expansion rules (mirrors backend
 * ``expand_permission_hierarchy``):
 *   - manage:<resource>[:scope]  →  write:<resource>[:scope]  →  read:<resource>[:scope]
 *   - knowledge:manage           →  knowledge:upload + approve + read + index_refs:read
 *   - knowledge:upload / approve →  knowledge:read
 *   - documents:* (v3 namespace) →  knowledge:* (v2 namespace bridge)
 *
 * Wildcard:
 *   - ``"*"`` is reserved for system roles (Owner, Admin) seeded
 *     server-side; it is intentionally excluded from ALL_PERMISSIONS so
 *     custom roles cannot grant it via the UI.
 */

// ─────────────────────────────────────────────────────────────────────
// PERMISSIONS — 1:1 with backend ``Permissions`` (api/app/core/permissions.py)
// ─────────────────────────────────────────────────────────────────────

/**
 * Canonical permission string constants. Use these everywhere instead
 * of inline string literals so renames stay safe.
 */
export const PERMISSIONS = {
  // ── Initiatives (with optional :all / :owned / :participating / :org_unit scope) ──
  INITIATIVES_READ: 'initiatives:read',
  INITIATIVES_WRITE: 'initiatives:write',
  INITIATIVES_MANAGE: 'initiatives:manage',

  // ── Members ──
  MEMBERS_READ: 'members:read',
  MEMBERS_WRITE: 'members:write',
  MEMBERS_MANAGE: 'members:manage',

  // ── Members sub-permissions (granted by MEMBERS_MANAGE in the role editor) ──
  INVITES_READ: 'invites:read',
  INVITES_CREATE: 'invites:create',
  INVITES_DELETE: 'invites:delete',
  ROLES_READ: 'roles:read',
  ROLES_CREATE: 'roles:create',
  ROLES_DELETE: 'roles:delete',
  ORG_UNITS_READ: 'org_units:read',
  ORG_UNITS_CREATE: 'org_units:create',
  ORG_UNITS_DELETE: 'org_units:delete',

  // ── System Settings ──
  SETTINGS_READ: 'settings:read',
  SETTINGS_WRITE: 'settings:write',

  // ── Support (with :all scope) ──
  SUPPORT_READ: 'support:read',
  SUPPORT_WRITE: 'support:write',
  SUPPORT_MANAGE: 'support:manage',

  // ── Ideas ──
  IDEAS_READ: 'ideas:read',
  IDEAS_WRITE: 'ideas:write',
  IDEAS_MANAGE: 'ideas:manage',

  // ── AI Leads ──
  AI_LEADS_READ: 'ai_leads:read',
  AI_LEADS_MANAGE: 'ai_leads:manage',

  // ── Vendors ──
  VENDORS_READ: 'vendors:read',
  VENDORS_WRITE: 'vendors:write',
  VENDORS_MANAGE: 'vendors:manage',

  // ── Cost Management ──
  COST_MANAGEMENT_READ: 'cost_management:read',
  COST_MANAGEMENT_MANAGE: 'cost_management:manage',

  // ── Integrations ──
  INTEGRATIONS_READ: 'integrations:read',
  INTEGRATIONS_MANAGE: 'integrations:manage',

  // ── Knowledge Base (manage → upload + approve + read + index_refs:read) ──
  KNOWLEDGE_READ: 'knowledge:read',
  KNOWLEDGE_UPLOAD: 'knowledge:upload',
  KNOWLEDGE_APPROVE: 'knowledge:approve',
  KNOWLEDGE_MANAGE: 'knowledge:manage',
  KNOWLEDGE_DEBUG: 'knowledge:debug',
  KNOWLEDGE_INDEX_REFS_READ: 'knowledge:index_refs:read',

  // ── E15 catalogue (seeded by alembic ``authz11perm``) ──
  POLICY_READ: 'policy:read',
  POLICY_CONFIGURE: 'policy:configure',
  TENANT_IMPERSONATE: 'tenant:impersonate',
  AUDIT_LOG_ENTRY_READ: 'audit_log_entry:read',
  IDP_CONFIGURE: 'idp:configure',
  // ── Access requests ── self-service tenant join workflow.
  // Held by Owner / Admin via the wildcard plus the seeded
  // ``Portal Administrator`` default role.
  ACCESS_REQUESTS_MANAGE: 'access_requests:manage',

  // ── AI Workspace skill catalogue ──
  // Each token is the per-skill PEP gate evaluated by the AI Workspace
  // runtime AFTER ChatSkillRouter selects a SkillKind. Wildcard ``*``
  // grants all skill gates; the explicit constants exist so the role editor
  // can render them and so custom roles can target a single skill.
  SKILL_RUN_GUIDE: 'skill:run:guide',
  SKILL_RUN_GENERAL_CHAT: 'skill:run:general_chat',
  SKILL_RUN_KNOWLEDGE_QA: 'skill:run:knowledge_qa',
  SKILL_RUN_PROMPT_SOLUTION_DISCOVERY: 'skill:run:prompt_solution_discovery',
  SKILL_RUN_PROMPT_SOLUTION_EXECUTION: 'skill:run:prompt_solution_execution',
  SKILL_RUN_LIGHT_TASK: 'skill:run:light_task',
  SKILL_RUN_USAGE_SUMMARY: 'skill:run:usage_summary',
  SKILL_RUN_IDEA_INTAKE: 'skill:run:idea_intake',
  SKILL_RUN_IDEA_STATUS: 'skill:run:idea_status',

  // ── AI Usage (workspace cost & usage analytics, with scope modifier) ──
  AI_USAGE_READ: 'ai_usage:read',
  AI_USAGE_READ_ALL: 'ai_usage:read:all',
  AI_USAGE_READ_ORG_UNIT: 'ai_usage:read:org_unit',
  AI_USAGE_READ_OWNED: 'ai_usage:read:owned',

  // ── Wildcard (system roles only — never grantable from the UI) ──
  ALL: '*',
} as const;

export type UsageScope = 'all' | 'org_unit' | 'owned';

export type PermissionKey = keyof typeof PERMISSIONS;
export type PermissionString = (typeof PERMISSIONS)[PermissionKey];

/**
 * Flat catalogue of every grantable permission string.
 * Wildcard (``*``) is intentionally excluded — it is reserved for
 * system roles seeded server-side and must not appear in custom role
 * definitions.
 */
export const ALL_PERMISSIONS: readonly string[] = [
  // Initiatives
  PERMISSIONS.INITIATIVES_READ,
  PERMISSIONS.INITIATIVES_WRITE,
  PERMISSIONS.INITIATIVES_MANAGE,
  // Members
  PERMISSIONS.MEMBERS_READ,
  PERMISSIONS.MEMBERS_WRITE,
  PERMISSIONS.MEMBERS_MANAGE,
  PERMISSIONS.INVITES_READ,
  PERMISSIONS.INVITES_CREATE,
  PERMISSIONS.INVITES_DELETE,
  PERMISSIONS.ROLES_READ,
  PERMISSIONS.ROLES_CREATE,
  PERMISSIONS.ROLES_DELETE,
  PERMISSIONS.ORG_UNITS_READ,
  PERMISSIONS.ORG_UNITS_CREATE,
  PERMISSIONS.ORG_UNITS_DELETE,
  // System Settings
  PERMISSIONS.SETTINGS_READ,
  PERMISSIONS.SETTINGS_WRITE,
  // Support
  PERMISSIONS.SUPPORT_READ,
  PERMISSIONS.SUPPORT_WRITE,
  PERMISSIONS.SUPPORT_MANAGE,
  // Ideas
  PERMISSIONS.IDEAS_READ,
  PERMISSIONS.IDEAS_WRITE,
  PERMISSIONS.IDEAS_MANAGE,
  // AI Leads
  PERMISSIONS.AI_LEADS_READ,
  PERMISSIONS.AI_LEADS_MANAGE,
  // Vendors
  PERMISSIONS.VENDORS_READ,
  PERMISSIONS.VENDORS_WRITE,
  PERMISSIONS.VENDORS_MANAGE,
  // Cost Management
  PERMISSIONS.COST_MANAGEMENT_READ,
  PERMISSIONS.COST_MANAGEMENT_MANAGE,
  // Integrations
  PERMISSIONS.INTEGRATIONS_READ,
  PERMISSIONS.INTEGRATIONS_MANAGE,
  // Knowledge
  PERMISSIONS.KNOWLEDGE_READ,
  PERMISSIONS.KNOWLEDGE_UPLOAD,
  PERMISSIONS.KNOWLEDGE_APPROVE,
  PERMISSIONS.KNOWLEDGE_MANAGE,
  PERMISSIONS.KNOWLEDGE_DEBUG,
  PERMISSIONS.KNOWLEDGE_INDEX_REFS_READ,
  // E15 catalogue
  PERMISSIONS.POLICY_READ,
  PERMISSIONS.POLICY_CONFIGURE,
  PERMISSIONS.TENANT_IMPERSONATE,
  PERMISSIONS.AUDIT_LOG_ENTRY_READ,
  PERMISSIONS.IDP_CONFIGURE,
  // Access requests
  PERMISSIONS.ACCESS_REQUESTS_MANAGE,
  // AI Workspace skill catalogue.
  PERMISSIONS.SKILL_RUN_GUIDE,
  PERMISSIONS.SKILL_RUN_GENERAL_CHAT,
  PERMISSIONS.SKILL_RUN_KNOWLEDGE_QA,
  PERMISSIONS.SKILL_RUN_PROMPT_SOLUTION_DISCOVERY,
  PERMISSIONS.SKILL_RUN_PROMPT_SOLUTION_EXECUTION,
  PERMISSIONS.SKILL_RUN_LIGHT_TASK,
  PERMISSIONS.SKILL_RUN_USAGE_SUMMARY,
  PERMISSIONS.SKILL_RUN_IDEA_INTAKE,
  PERMISSIONS.SKILL_RUN_IDEA_STATUS,
  // AI Usage (with scope modifier — owned/org_unit/all)
  PERMISSIONS.AI_USAGE_READ,
  PERMISSIONS.AI_USAGE_READ_ALL,
  PERMISSIONS.AI_USAGE_READ_ORG_UNIT,
  PERMISSIONS.AI_USAGE_READ_OWNED,
] as const;

// ─────────────────────────────────────────────────────────────────────
// Initiative scope modifiers (used by the role editor scope dropdown)
// Values mirror backend ``INITIATIVE_SCOPES``.
// ─────────────────────────────────────────────────────────────────────

export const INITIATIVE_SCOPES = [
  { value: 'all', label: 'All Initiatives' },
  { value: 'org_unit', label: 'Org unit only' },
  { value: 'owned', label: 'Owned Only' },
  { value: 'participating', label: 'Participating Only' },
] as const;

export type InitiativeScope = (typeof INITIATIVE_SCOPES)[number]['value'];

// ─────────────────────────────────────────────────────────────────────
// Permission group definitions for the role editor UI
//
// Each group represents a resource (initiatives, members, settings,
// support, ideas, ai_leads, integrations, knowledge). The editor
// renders one card per group with level + scope + sub-permissions.
// ─────────────────────────────────────────────────────────────────────

export type PermissionLevel = { value: string; label: string };
export type PermissionScope = { value: string; label: string };
export type PermissionAction = { value: string; label: string };
export type SubPermissionDef = {
  id: string;
  label: string;
  actions: PermissionAction[];
  /** Lowest level (within the parent group) that unlocks this sub-permission. */
  requiredLevel: string;
};

export type PermissionGroupDef = {
  id: string;
  label: string;
  levels: PermissionLevel[];
  scopes?: PermissionScope[];
  subPermissions?: SubPermissionDef[];
};

export const PERMISSION_GROUPS: PermissionGroupDef[] = [
  {
    id: 'initiatives',
    label: 'Initiatives',
    levels: [
      { value: 'read', label: 'Read' },
      { value: 'write', label: 'Write' },
      { value: 'manage', label: 'Manage' },
    ],
    scopes: [
      { value: 'all', label: 'All' },
      { value: 'org_unit', label: 'Org unit' },
      { value: 'owned', label: 'Owned' },
      { value: 'participating', label: 'Participating' },
    ],
  },
  {
    id: 'members',
    label: 'Members',
    levels: [
      { value: 'read', label: 'Read' },
      { value: 'write', label: 'Write' },
      { value: 'manage', label: 'Manage' },
    ],
    subPermissions: [
      {
        id: 'invites',
        label: 'Invites',
        actions: [
          { value: 'read', label: 'Read' },
          { value: 'create', label: 'Create' },
          { value: 'delete', label: 'Delete' },
        ],
        requiredLevel: 'manage',
      },
      {
        id: 'roles',
        label: 'Roles',
        actions: [
          { value: 'read', label: 'Read' },
          { value: 'create', label: 'Create' },
          { value: 'delete', label: 'Delete' },
        ],
        requiredLevel: 'manage',
      },
      {
        id: 'org_units',
        label: 'Org Structure',
        actions: [
          { value: 'read', label: 'Read' },
          { value: 'create', label: 'Create' },
          { value: 'delete', label: 'Delete' },
        ],
        requiredLevel: 'manage',
      },
    ],
  },
  {
    id: 'settings',
    label: 'System Settings',
    levels: [
      { value: 'read', label: 'Read' },
      { value: 'write', label: 'Write' },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    levels: [
      { value: 'read', label: 'Read' },
      { value: 'write', label: 'Write' },
      { value: 'manage', label: 'Manage' },
    ],
    scopes: [{ value: 'all', label: 'All' }],
  },
  {
    id: 'ideas',
    label: 'Ideas',
    levels: [
      { value: 'read', label: 'Read' },
      { value: 'write', label: 'Write' },
      { value: 'manage', label: 'Manage' },
    ],
  },
  {
    id: 'ai_leads',
    label: 'AI Leads',
    levels: [
      { value: 'read', label: 'Read' },
      { value: 'manage', label: 'Manage' },
    ],
  },
  {
    id: 'vendors',
    label: 'Vendors',
    levels: [
      { value: 'read', label: 'Read' },
      { value: 'write', label: 'Write' },
      { value: 'manage', label: 'Manage' },
    ],
  },
  {
    id: 'cost_management',
    label: 'Cost Management',
    levels: [
      { value: 'read', label: 'Read' },
      { value: 'manage', label: 'Manage' },
    ],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    levels: [
      { value: 'read', label: 'Read' },
      { value: 'manage', label: 'Manage' },
    ],
  },
  {
    id: 'knowledge',
    label: 'Knowledge Base',
    levels: [
      { value: 'read', label: 'Read' },
      { value: 'upload', label: 'Upload' },
      { value: 'approve', label: 'Approve' },
      { value: 'manage', label: 'Manage' },
    ],
  },
  {
    id: 'ai_usage',
    label: 'AI Usage',
    levels: [
      { value: 'read', label: 'Read' },
    ],
    scopes: [
      { value: 'all', label: 'All (tenant)' },
      { value: 'org_unit', label: 'Org unit' },
      { value: 'owned', label: 'Owned' },
    ],
  },
];

/**
 * ``PERMISSIONS_BY_GROUP`` maps each group id to the list of canonical
 * permission strings it owns. Useful for tests, static analysis, and
 * any UI that wants to iterate the catalogue grouped by resource
 * without parsing the editor metadata.
 */
export const PERMISSIONS_BY_GROUP: Readonly<Record<string, readonly string[]>> = Object.freeze({
  initiatives: [
    PERMISSIONS.INITIATIVES_READ,
    PERMISSIONS.INITIATIVES_WRITE,
    PERMISSIONS.INITIATIVES_MANAGE,
  ],
  members: [
    PERMISSIONS.MEMBERS_READ,
    PERMISSIONS.MEMBERS_WRITE,
    PERMISSIONS.MEMBERS_MANAGE,
    PERMISSIONS.INVITES_READ,
    PERMISSIONS.INVITES_CREATE,
    PERMISSIONS.INVITES_DELETE,
    PERMISSIONS.ROLES_READ,
    PERMISSIONS.ROLES_CREATE,
    PERMISSIONS.ROLES_DELETE,
    PERMISSIONS.ORG_UNITS_READ,
    PERMISSIONS.ORG_UNITS_CREATE,
    PERMISSIONS.ORG_UNITS_DELETE,
  ],
  settings: [PERMISSIONS.SETTINGS_READ, PERMISSIONS.SETTINGS_WRITE],
  support: [
    PERMISSIONS.SUPPORT_READ,
    PERMISSIONS.SUPPORT_WRITE,
    PERMISSIONS.SUPPORT_MANAGE,
  ],
  ideas: [PERMISSIONS.IDEAS_READ, PERMISSIONS.IDEAS_WRITE, PERMISSIONS.IDEAS_MANAGE],
  ai_leads: [PERMISSIONS.AI_LEADS_READ, PERMISSIONS.AI_LEADS_MANAGE],
  vendors: [
    PERMISSIONS.VENDORS_READ,
    PERMISSIONS.VENDORS_WRITE,
    PERMISSIONS.VENDORS_MANAGE,
  ],
  cost_management: [
    PERMISSIONS.COST_MANAGEMENT_READ,
    PERMISSIONS.COST_MANAGEMENT_MANAGE,
  ],
  integrations: [PERMISSIONS.INTEGRATIONS_READ, PERMISSIONS.INTEGRATIONS_MANAGE],
  knowledge: [
    PERMISSIONS.KNOWLEDGE_READ,
    PERMISSIONS.KNOWLEDGE_UPLOAD,
    PERMISSIONS.KNOWLEDGE_APPROVE,
    PERMISSIONS.KNOWLEDGE_MANAGE,
    PERMISSIONS.KNOWLEDGE_DEBUG,
    PERMISSIONS.KNOWLEDGE_INDEX_REFS_READ,
  ],
  // E15 catalogue lives under several resource buckets so the consumer
  // can render distinct UI sections (governance / impersonation /
  // audit / sso / skills).
  policy: [PERMISSIONS.POLICY_READ, PERMISSIONS.POLICY_CONFIGURE],
  tenant: [PERMISSIONS.TENANT_IMPERSONATE],
  audit_log_entry: [PERMISSIONS.AUDIT_LOG_ENTRY_READ],
  idp: [PERMISSIONS.IDP_CONFIGURE],
  access_requests: [PERMISSIONS.ACCESS_REQUESTS_MANAGE],
  skill: [
    PERMISSIONS.SKILL_RUN_GUIDE,
    PERMISSIONS.SKILL_RUN_GENERAL_CHAT,
    PERMISSIONS.SKILL_RUN_KNOWLEDGE_QA,
    PERMISSIONS.SKILL_RUN_PROMPT_SOLUTION_DISCOVERY,
    PERMISSIONS.SKILL_RUN_PROMPT_SOLUTION_EXECUTION,
    PERMISSIONS.SKILL_RUN_LIGHT_TASK,
    PERMISSIONS.SKILL_RUN_USAGE_SUMMARY,
    PERMISSIONS.SKILL_RUN_IDEA_INTAKE,
    PERMISSIONS.SKILL_RUN_IDEA_STATUS,
  ],
  ai_usage: [
    PERMISSIONS.AI_USAGE_READ,
    PERMISSIONS.AI_USAGE_READ_ALL,
    PERMISSIONS.AI_USAGE_READ_ORG_UNIT,
    PERMISSIONS.AI_USAGE_READ_OWNED,
  ],
});

// ─────────────────────────────────────────────────────────────────────
// Permission labels (display strings for badges, tables, role cards)
// ─────────────────────────────────────────────────────────────────────

export const PERMISSION_LABELS: Record<string, string> = {
  'initiatives:read': 'Initiatives — Read',
  'initiatives:write': 'Initiatives — Write',
  'initiatives:manage': 'Initiatives — Manage',
  'members:read': 'Members — Read',
  'members:write': 'Members — Write',
  'members:manage': 'Members — Manage',
  'invites:read': 'Invites — Read',
  'invites:create': 'Invites — Create',
  'invites:delete': 'Invites — Delete',
  'roles:read': 'Roles — Read',
  'roles:create': 'Roles — Create',
  'roles:delete': 'Roles — Delete',
  'org_units:read': 'Org Structure — Read',
  'org_units:create': 'Org Structure — Create',
  'org_units:delete': 'Org Structure — Delete',
  'settings:read': 'Settings — Read',
  'settings:write': 'Settings — Write',
  'support:read': 'Support — Read',
  'support:write': 'Support — Write',
  'support:manage': 'Support — Manage',
  'ideas:read': 'Ideas — Read',
  'ideas:write': 'Ideas — Write',
  'ideas:manage': 'Ideas — Manage',
  'ai_leads:read': 'AI Leads — Read',
  'ai_leads:manage': 'AI Leads — Manage',
  'vendors:read': 'Vendors — Read',
  'vendors:write': 'Vendors — Write',
  'vendors:manage': 'Vendors — Manage',
  'cost_management:read': 'Cost Management — Read',
  'cost_management:manage': 'Cost Management — Manage',
  'integrations:read': 'Integrations — Read',
  'integrations:manage': 'Integrations — Manage',
  'knowledge:read': 'Knowledge — Read',
  'knowledge:upload': 'Knowledge — Upload',
  'knowledge:approve': 'Knowledge — Approve',
  'knowledge:manage': 'Knowledge — Manage',
  'knowledge:debug': 'Knowledge — Debug',
  'knowledge:index_refs:read': 'Knowledge — Index Refs Read',
  'policy:read': 'Policy — Read',
  'policy:configure': 'Policy — Configure',
  'tenant:impersonate': 'Tenant — Impersonate',
  'audit_log_entry:read': 'Audit Log — Read',
  'idp:configure': 'Identity Provider — Configure',
  'access_requests:manage': 'Access Requests — Manage',
  'skill:run:guide': 'Skill — Run Guide',
  'skill:run:general_chat': 'Skill — Run General Chat',
  'skill:run:knowledge_qa': 'Skill — Run Knowledge Q&A',
  'skill:run:prompt_solution_discovery': 'Skill — Run Prompt/Solution Discovery',
  'skill:run:prompt_solution_execution': 'Skill — Run Prompt Solution Execution',
  'skill:run:light_task': 'Skill — Run Light Task',
  'skill:run:usage_summary': 'Skill — Run Usage Summary',
  'skill:run:idea_intake': 'Skill — Run Idea Intake',
  'skill:run:idea_status': 'Skill — Run Idea Status',
  'ai_usage:read': 'AI Usage — Read',
  'ai_usage:read:all': 'AI Usage — Read (all)',
  'ai_usage:read:org_unit': 'AI Usage — Read (org unit)',
  'ai_usage:read:owned': 'AI Usage — Read (owned)',
};

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

/**
 * Parse a stored permission string into its components.
 *
 * @example
 *   parsePermission("initiatives:read:all")
 *     → { resource: "initiatives", action: "read", scope: "all" }
 *   parsePermission("members:manage")
 *     → { resource: "members", action: "manage", scope: undefined }
 */
export function parsePermission(perm: string): {
  resource: string;
  action: string;
  scope?: string;
} {
  const parts = perm.split(':');
  return { resource: parts[0], action: parts[1], scope: parts[2] };
}

/**
 * Minimal auth-context shape ``hasPermission`` operates on. Mirrors
 * ``AuthContext.user.permissions`` (which is the flat list returned by
 * ``GET /auth/session``).
 */
export interface AuthContextLike {
  permissions?: readonly string[] | null;
}

/**
 * Return true when ``ctx`` (or a flat permission list) holds ``perm``.
 *
 * Matches the backend ``AuthContext.has_permission`` logic
 * (``api/app/modules/auth/application/security.py``):
 *   1. The wildcard ``*`` always matches
 *   2. An exact match wins
 *   3. A scoped grant satisfies a scope-less request
 *      e.g. ``initiatives:read:all`` satisfies ``initiatives:read``
 *
 * IMPORTANT:
 *   ``hasPermission(ctx, "initiatives:write")`` returns true for a user
 *   who only holds ``initiatives:write:owned``. This mirrors backend
 *   semantics and is INTENTIONAL — but it means call sites that need to
 *   gate a per-record action (e.g. the EditButton on a single initiative)
 *   MUST also check the user's effective scope against the resource via
 *   ``getPermissionScope`` and the resource owner / org_unit / etc.
 *
 * Accepts either an auth-context-like object (``{ permissions: [...] }``)
 * or a raw array of permission strings — handy for tests and call sites
 * that already destructured the list.
 */
export function hasPermission(
  ctx: AuthContextLike | readonly string[] | null | undefined,
  perm: string,
): boolean {
  if (!ctx) return false;
  const perms: readonly string[] = Array.isArray(ctx)
    ? ctx
    : (ctx as AuthContextLike).permissions ?? [];

  if (perms.includes(PERMISSIONS.ALL)) return true;
  if (perms.includes(perm)) return true;
  // Scoped grant satisfies a scope-less query
  // (e.g. "initiatives:read:all" matches "initiatives:read")
  return perms.some((p) => p.startsWith(perm + ':'));
}

/**
 * Broadest scope a user holds on ``perm``, mirroring backend
 * ``AuthContext.get_permission_scope`` in
 * ``api/app/modules/auth/application/security.py``.
 *
 * Priority (broadest → narrowest):
 *   ``all`` > ``org_unit`` > ``participating`` > ``owned``
 *
 * Returns:
 *   - ``"all"`` when the user holds the wildcard ``*`` or an unscoped
 *     legacy grant for ``perm`` (treated as full breadth, same as the
 *     backend)
 *   - the broadest scope string when the user holds one or more scoped
 *     variants (``perm:<scope>``)
 *   - ``null`` when the user does not hold the permission at all
 *
 * Used by call sites that gate per-record actions (e.g. the EditButton
 * on a single initiative). Combined with the resource's owner /
 * org_unit / participant list this lets the UI show the action only
 * when the backend would actually authorise the save — preventing the
 * "button visible but submit returns 403" UX bug.
 *
 * @example
 *   getPermissionScope({ permissions: ['initiatives:write:owned'] }, 'initiatives:write')
 *     → 'owned'
 *   getPermissionScope({ permissions: ['initiatives:write:all', 'initiatives:write:owned'] }, 'initiatives:write')
 *     → 'all'
 *   getPermissionScope({ permissions: ['*'] }, 'initiatives:write')
 *     → 'all'
 *   getPermissionScope({ permissions: ['ideas:read'] }, 'initiatives:write')
 *     → null
 */
export type InitiativeScopeValue = 'all' | 'org_unit' | 'participating' | 'owned';

const SCOPE_PRIORITY: Readonly<Record<string, number>> = Object.freeze({
  all: 4,
  // Keyed by the permission scope VALUE, which is "org_unit"
  // (the stored scope token; see InitiativeScopeValue / UsageScope).
  org_unit: 3,
  participating: 2,
  owned: 1,
});

export function getPermissionScope(
  ctx: AuthContextLike | readonly string[] | null | undefined,
  perm: string,
): InitiativeScopeValue | null {
  if (!ctx) return null;
  const perms: readonly string[] = Array.isArray(ctx)
    ? ctx
    : (ctx as AuthContextLike).permissions ?? [];

  if (perms.includes(PERMISSIONS.ALL)) return 'all';
  // Unscoped legacy grant → broadest scope, mirroring backend.
  if (perms.includes(perm)) return 'all';

  const prefix = perm + ':';
  let best: string | null = null;
  for (const p of perms) {
    if (!p.startsWith(prefix)) continue;
    const scope = p.slice(prefix.length);
    if (
      best === null ||
      (SCOPE_PRIORITY[scope] ?? 0) > (SCOPE_PRIORITY[best] ?? 0)
    ) {
      best = scope;
    }
  }
  return (best as InitiativeScopeValue | null) ?? null;
}

/**
 * Per-record gate for an initiative-level action (e.g. EditButton on a
 * single initiative). Returns true ONLY when the user's broadest scope
 * for ``perm`` actually applies to ``record`` — preventing the bug
 * where users with ``initiatives:write:owned`` saw the EditButton on
 * initiatives they didn't own (UI showed enabled action that 403'd at
 * submit time).
 *
 * Scope semantics (mirror backend
 * ``api/app/modules/auth/application/security.py``):
 *   - ``all``           → permitted on every initiative
 *   - ``org_unit``    → permitted when the user shares any org_unit
 *                         with the initiative (single ``org_unit`` or
 *                         multi ``org_units``)
 *   - ``participating`` → permitted when the user appears on the
 *                         initiative's ``team`` / participant list, OR
 *                         is the owner
 *   - ``owned``         → permitted only when the user is the owner
 *   - null              → permission not held at all
 *
 * The shape arguments are intentionally minimal so the helper is reusable
 * across initiative pages without dragging in the full ``UseCase`` type.
 */
export interface InitiativeRecordLike {
  ownerUserId?: string | null;
  org_unit?: string | null;
  org_units?: readonly string[] | null;
  team?: readonly string[] | null;
}

export interface InitiativeViewerLike {
  user_id?: string | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  org_units?: readonly string[] | null;
  permissions?: readonly string[] | null;
}

export function canActOnInitiative(
  viewer: InitiativeViewerLike | null | undefined,
  record: InitiativeRecordLike | null | undefined,
  perm: string,
): boolean {
  if (!viewer || !record) return false;
  const scope = getPermissionScope(viewer, perm);
  if (scope === null) return false;
  if (scope === 'all') return true;

  if (scope === 'owned') {
    return Boolean(viewer.user_id && record.ownerUserId === viewer.user_id);
  }

  if (scope === 'org_unit') {
    const userDepts = new Set(viewer.org_units ?? []);
    if (userDepts.size === 0) return false;
    if (record.org_unit && userDepts.has(record.org_unit)) return true;
    for (const d of record.org_units ?? []) {
      if (userDepts.has(d)) return true;
    }
    return false;
  }

  if (scope === 'participating') {
    // Owner is implicitly participating.
    if (viewer.user_id && record.ownerUserId === viewer.user_id) return true;
    const team = record.team ?? [];
    if (team.length === 0) return false;
    // ``team`` stores display names (see backend participant sync) so
    // try every reasonable identifier the viewer might appear under:
    // user_id, email, "First Last", first/last alone.
    const candidates = new Set<string>();
    if (viewer.user_id) candidates.add(viewer.user_id);
    if (viewer.email) candidates.add(viewer.email);
    if (viewer.first_name && viewer.last_name) {
      candidates.add(`${viewer.first_name} ${viewer.last_name}`);
    }
    if (viewer.first_name) candidates.add(viewer.first_name);
    if (viewer.last_name) candidates.add(viewer.last_name);
    return team.some((t) => candidates.has(t));
  }

  return false;
}

/**
 * Expand a raw set of permission strings into the full effective set
 * by applying the same hierarchy rules the backend uses.
 *
 * Mirrors ``app.modules.authz.application.permissions.expand_permission_hierarchy``:
 *   1. Hierarchy expansion: ``manage:<r>[:<s>]`` →
 *      ``write:<r>[:<s>]`` → ``read:<r>[:<s>]``
 *   2. Knowledge-specific: ``manage`` → ``upload + approve + read +
 *      index_refs:read``; ``upload`` / ``approve`` → ``read``
 *   3. Documents (knowledge v2) hierarchy and the v3 → v2 bridge
 *      from ``documents:*`` to ``knowledge:*``.
 *
 * Returns a ``ReadonlySet<string>`` so callers cannot mutate the
 * cached result.
 */
export function expandPermissionHierarchy(
  rawPerms: Iterable<string>,
): ReadonlySet<string> {
  const expanded = new Set<string>(rawPerms);

  // 1. Hierarchy expansion: manage → write → read (preserving scope).
  for (const perm of Array.from(expanded)) {
    const parts = perm.split(':');
    if (parts.length < 2) continue;
    const [resource, action] = parts;
    const scope = parts.length > 2 ? parts.slice(2).join(':') : undefined;
    const suffix = scope ? `:${scope}` : '';
    if (action === 'manage') {
      expanded.add(`${resource}:write${suffix}`);
      expanded.add(`${resource}:read${suffix}`);
    } else if (action === 'write') {
      expanded.add(`${resource}:read${suffix}`);
    }
  }

  // 2. Knowledge-specific hierarchy.
  if (expanded.has('knowledge:manage')) {
    expanded.add('knowledge:upload');
    expanded.add('knowledge:approve');
    expanded.add('knowledge:read');
    expanded.add('knowledge:index_refs:read');
  }
  if (expanded.has('knowledge:upload')) {
    expanded.add('knowledge:read');
  }
  if (expanded.has('knowledge:approve')) {
    expanded.add('knowledge:read');
  }

  // 3. Documents (Knowledge v2) hierarchy.
  if (expanded.has('documents:manage')) {
    expanded.add('documents:upload');
    expanded.add('documents:read');
  }
  if (expanded.has('documents:upload')) {
    expanded.add('documents:read');
  }

  // 4. Bridge from the v3 ``documents:*`` namespace to the legacy
  // ``knowledge:*`` route guards. Mirrors the backend bridge in
  // ``expand_permission_hierarchy``.
  if (expanded.has('documents:manage')) {
    expanded.add('knowledge:manage');
    expanded.add('knowledge:upload');
    expanded.add('knowledge:approve');
    expanded.add('knowledge:read');
    expanded.add('knowledge:index_refs:read');
  }
  if (expanded.has('documents:upload')) {
    expanded.add('knowledge:upload');
    expanded.add('knowledge:read');
  }
  if (expanded.has('documents:read')) {
    expanded.add('knowledge:read');
  }

  return expanded;
}

// ─────────────────────────────────────────────────────────────────────
// Editor state helpers — used by AdminRoleEdit.tsx to round-trip a
// permission list through the role editor UI.
// ─────────────────────────────────────────────────────────────────────

export type EditorGroupState = {
  level: string | null;
  scope: string | null;
  subPerms: Record<string, Set<string>>;
};

/**
 * Given a flat list of permission strings, extract the structured state
 * used by the role editor.
 */
export function permissionsToEditorState(
  perms: readonly string[],
): Record<string, EditorGroupState> {
  const state: Record<string, EditorGroupState> = {};

  for (const group of PERMISSION_GROUPS) {
    const groupState: EditorGroupState = {
      level: null,
      scope: null,
      subPerms: {},
    };

    // Find the highest level selected for this group.
    for (const lvl of [...group.levels].reverse()) {
      const base = `${group.id}:${lvl.value}`;
      const match = perms.find((p) => p === base || p.startsWith(base + ':'));
      if (match) {
        groupState.level = lvl.value;
        const parsed = parsePermission(match);
        if (parsed.scope) {
          groupState.scope = parsed.scope;
        }
        break;
      }
    }

    // Sub-permissions
    if (group.subPermissions) {
      for (const sub of group.subPermissions) {
        const actions = new Set<string>();
        for (const act of sub.actions) {
          if (perms.includes(`${sub.id}:${act.value}`)) {
            actions.add(act.value);
          }
        }
        groupState.subPerms[sub.id] = actions;
      }
    }

    state[group.id] = groupState;
  }

  return state;
}

/**
 * Convert editor state back to a flat list of permission strings.
 */
export function editorStateToPermissions(
  state: Record<string, EditorGroupState>,
): string[] {
  const perms: string[] = [];

  for (const group of PERMISSION_GROUPS) {
    const gs = state[group.id];
    if (!gs || !gs.level) continue;

    let perm = `${group.id}:${gs.level}`;
    if (group.scopes && gs.scope) {
      perm += `:${gs.scope}`;
    }
    perms.push(perm);

    // Sub-permissions only emitted when current level meets the
    // required threshold for that sub-permission.
    if (group.subPermissions) {
      for (const sub of group.subPermissions) {
        if (
          gs.level === sub.requiredLevel ||
          isLevelHigherOrEqual(gs.level, sub.requiredLevel, group.levels)
        ) {
          const actions = gs.subPerms[sub.id];
          if (actions) {
            for (const act of actions) {
              perms.push(`${sub.id}:${act}`);
            }
          }
        }
      }
    }
  }

  return perms;
}

function isLevelHigherOrEqual(
  current: string,
  required: string,
  levels: readonly { value: string }[],
): boolean {
  const currentIdx = levels.findIndex((l) => l.value === current);
  const requiredIdx = levels.findIndex((l) => l.value === required);
  return currentIdx >= requiredIdx;
}
